import { useCallback, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router";

import type { Room, RoomDummy } from "@/lib/gql/__generated__/graphql";

import type { DnDResultItem } from "@/hooks/use-dnd/use-dnd";

import { PATHS } from "@/routes/paths";

import { BlankContentForList } from "@/components/features/_components";
import { RoomHeaderAndTabs } from "@/components/features/items/_components";
import {
	ListTableHeader,
	SortButton,
} from "@/components/features/items/edit/_shared/ui/components/system/list/list-table-header";
import { ItemListContainer } from "@/components/features/items/edit/_shared/ui/components/system/list/list-wrapper";

import { ExternalLink } from "@/components/ui/button/external-link";
import { Table, TableBody, TableCaption } from "@/components/ui/table/table";

import { RoomListPageTableDefaultItem, RoomListTableHeader } from "./table";
import { RoomListPageTableSortableItem } from "./table/sortable-item";

type Props = {
	hotelID: string;
	onSaveSort: () => Promise<void>;
	onCancelSort: () => void;

	// editRoomPagePath: (isDummy: { isDummy: boolean }) => {
	// 	edit: (roomID: string) => string;
	// 	createNewItem: string;
	// };

	handleRoomDelete: (roomID: string) => void;

	dndItemList: Array<DnDResultItem<Room>>;
	dummyRooms: RoomDummy[];

	inUseBasic: boolean;
};

// 通常部屋とダミー部屋の違いを説明するNOTION URL
const NOTION_URL = "https://www.notion.so/26ffffe15c5780159e32f5a845e2b123";

export const RoomListTable = ({
	hotelID,
	onSaveSort,
	onCancelSort,
	// editRoomPagePath,
	dndItemList,
	dummyRooms,
	handleRoomDelete,
	inUseBasic,
}: Props) => {
	const navigate = useNavigate();

	const createNewDefaultRoomPagePath = useMemo(
		() => generatePath(PATHS.HOTEL.ITEMS.ROOM.EDIT.NEW.ROOM.DEFAULT, { hotelID }),
		[hotelID],
	);
	const createNewDummyRoomPagePath = useMemo(
		() => generatePath(PATHS.HOTEL.ITEMS.ROOM.EDIT.NEW.DUMMY.DEFAULT, { hotelID }),
		[hotelID],
	);

	const createNewItemList = useMemo(
		() => [
			{
				title: "通常の部屋",
				handleClick: () => {
					navigate(createNewDefaultRoomPagePath);
				},
			},
			{
				title: "ダミー部屋(日帰り用)",
				handleClick: () => {
					navigate(createNewDummyRoomPagePath);
				},
				canShow: inUseBasic,
			},
		],
		[createNewDefaultRoomPagePath, createNewDummyRoomPagePath, inUseBasic, navigate],
	);

	/**
	 * 通常の部屋をリストで表示できるか
	 */
	const canShowRoomList = useMemo(() => dndItemList.length > 0, [dndItemList.length]);
	/**
	 *  ダミー部屋をリストで表示できるか
	 */
	const canShowDummyRoomList = useMemo(() => dummyRooms.length > 0, [dummyRooms.length]);

	const [isSorting, setIsSorting] = useState(false);
	const onChangeIsSorting = useCallback((isSorting: boolean) => setIsSorting(isSorting), []);

	return (
		<>
			<RoomHeaderAndTabs hotelID={hotelID} headerProps={{ title: "部屋の一覧" }} />

			<ItemListContainer
				header={
					<>
						<div>
							<ExternalLink href={NOTION_URL} text="通常部屋とダミー部屋の違いとは？" />
						</div>
						<ListTableHeader
							leftContent={
								<SortButton
									isSorting={isSorting}
									onChangeIsSorting={onChangeIsSorting}
									onClickCancelSort={onCancelSort}
									onClickSaveSort={onSaveSort}
								/>
							}
							createNewItemList={createNewItemList}
						/>
					</>
				}
				tableBody={
					<>
						<Table className="w-full min-w-[max-content]">
							<TableCaption>通常の部屋</TableCaption>
							{canShowRoomList ? (
								<>
									<RoomListTableHeader hasSortItem isSorting={isSorting} />
									<TableBody>
										{dndItemList.map((result) => {
											return (
												<RoomListPageTableSortableItem
													hotelID={hotelID}
													onDeleteRoom={handleRoomDelete}
													key={result.key}
													room={result}
													isSorting={isSorting}
												/>
											);
										})}
									</TableBody>
								</>
							) : (
								<BlankContentForList
									content={`部屋がありません。\n新規作成ボタンから部屋を作成してください。`}
								/>
							)}
						</Table>

						<Table className="w-full min-w-[max-content]">
							<TableCaption>ダミーの部屋</TableCaption>
							{canShowDummyRoomList ? (
								<>
									<RoomListTableHeader isSorting={isSorting} />
									<TableBody>
										{dummyRooms.map((item) => {
											return (
												<RoomListPageTableDefaultItem
													hotelID={hotelID}
													key={item.roomID}
													room={item}
													onDeleteRoom={handleRoomDelete}
												/>
											);
										})}
									</TableBody>
								</>
							) : (
								<BlankContentForList content="ダミー部屋がありません。" />
							)}
						</Table>
					</>
				}
			/>
		</>
	);
};
